/**
 * Token-renewal outcome — the missing half of the 401 story.
 *
 * `AuthProvider`'s token getter must return `string | null` (four call sites in api/client read it), so
 * when `getAccessTokenSilently()` throws, the reason is destroyed: EVERY failure class collapses to the
 * same `null`, the request goes out with no Bearer, the backend answers 401, and the session-expired
 * handler reads "no token was sent" as a confirmed-dead refresh and ejects the user. That is correct for
 * an expired/revoked refresh token — and wrong for a Web Locks contention timeout, an offline blip, a
 * DNS hiccup or an Auth0 429, all of which clear by themselves within seconds. Pool screens poll every
 * 60s, so the odds of hitting one of those are not academic.
 *
 * This module is the side channel that carries the WHY across that `null`: the getter classifies and
 * records the failure here, api/client reads it the instant the getter returns and pins it onto that
 * request's ApiError, and `reportIfSessionExpired` forwards the captured value onto the signal. (The
 * capture is what makes the record safe to share: read late, at 401 time, an overlapping renewal can
 * already have cleared it.) Deliberately React-free and module-scoped for the same reason as
 * sessionExpiredSignal — the QueryClient (and therefore the 401 detector) is built before any provider
 * mounts, so neither side of this seam can be a hook.
 *
 * Bias: anything not positively identified as dead is TRANSIENT. A transient classification only buys
 * one extra silent refresh attempt, rate-limited by TRANSIENT_RECOVERY_COOLDOWN_MS in
 * SessionExpiredProvider; if that attempt fails the user still lands on the overlay. So a wrong
 * "transient" costs one round-trip, while a wrong "dead" costs the user their session — the asymmetry
 * decides the default.
 */

export type RenewalFailure = "transient" | "dead";

/**
 * Auth0 error codes that identify a session which genuinely needs an INTERACTIVE login — no amount of
 * retrying gets a token back. `login_required` / `consent_required` / `interaction_required` are the
 * OIDC prompt=none family; `invalid_grant` is the rotating refresh token being expired, revoked, or
 * already-used (rotation reuse detection); `missing_refresh_token` is auth0-spa-js finding no refresh
 * token in the cache at all; `unauthorized` / `access_denied` are the tenant refusing this user.
 *
 * NOT here, on purpose: `timeout` (auth0-spa-js throws TimeoutError — `error === "timeout"` — both on a
 * slow token endpoint AND when `runWithLock` gives up after 10 attempts at the getTokenSilently lock,
 * i.e. another tab was renewing), `missing_scopes`, `request_error`, and every network/fetch rejection.
 * Those are exactly the self-healing cases this whole module exists to stop ejecting people over.
 */
const DEAD_ERROR_CODES: ReadonlySet<string> = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "missing_refresh_token",
  "unauthorized",
  "access_denied",
]);

/**
 * Pull the OAuth error code off whatever `getAccessTokenSilently()` rejected with.
 *
 * The real shapes, verified against the installed SDKs: auth0-spa-js throws `GenericError` (and its
 * subclasses `TimeoutError`, `MissingRefreshTokenError`, `MfaRequiredError`, …), all of which are Error
 * subclasses carrying `error: string` + `error_description: string`; auth0-react's `normalizeErrorFn`
 * passes any `Error` straight through and otherwise wraps a bare `{error, error_description}` object in
 * its own `OAuthError`, which has the same `error` field. So `.error` is the ONE field worth matching —
 * `message` is human copy ("Timeout", "Missing Refresh Token (audience: …)") and must never be parsed.
 *
 * Everything else — a bare string, null/undefined, a DOMException, a plain `Error("boom")` — yields no
 * code and therefore reads as transient, which is the safe default (see the module comment).
 */
function readErrorCode(e: unknown): string | null {
  if (typeof e !== "object" || e === null) return null;
  const code = (e as { error?: unknown }).error;
  return typeof code === "string" ? code : null;
}

/** Decide whether a failed silent renewal means "sign in again" (dead) or "try once more" (transient). */
export function classifyRenewalError(e: unknown): RenewalFailure {
  const code = readErrorCode(e);
  return code !== null && DEAD_ERROR_CODES.has(code) ? "dead" : "transient";
}

interface RenewalRecord {
  kind: RenewalFailure;
  at: number;
}

/** The single most recent renewal failure. Not a queue: concurrent requests share one verdict. */
let record: RenewalRecord | null = null;

/**
 * Monotonic counter, bumped by every `recordRenewalFailure`. It exists because the single record slot is
 * shared by renewals that are NOT in lockstep: auth0-spa-js dedupes only STRICTLY concurrent
 * getTokenSilently calls and drops the dedupe entry the moment one settles, so a renewal that starts a
 * few milliseconds later is a separate call with its own outcome. Without the counter, that later
 * renewal succeeding would call `clearRenewalFailure()` and erase the failure belonging to a request
 * that is still in flight — the 401 lands, finds an empty slot, and the user is ejected on a session
 * that just demonstrably renewed. The success path captures this number BEFORE it starts and clears only
 * if nothing was recorded since (see `clearRenewalFailure`).
 */
let sequence = 0;

/** The current record sequence — capture it before a renewal starts, hand it back to
 *  `clearRenewalFailure` on success. Opaque: only equality against a later read is meaningful. */
export function renewalSequence(): number {
  return sequence;
}

/** How long a record stays meaningful. It is written by the token getter and read by the 401 detector
 *  one round-trip later, so the window only has to cover a request — but it must be short enough that a
 *  failure from a screen the user left ten minutes ago can never explain today's 401. */
const DEFAULT_MAX_AGE_MS = 15_000;

/** Record why the last silent renewal failed. Called from the token getter's catch, before it returns
 *  null — the classification is only useful if it is stored BEFORE the request goes out untokenized. */
export function recordRenewalFailure(kind: RenewalFailure): void {
  sequence += 1;
  record = { kind, at: Date.now() };
}

/**
 * Drop the record on a SUCCESSFUL token fetch. Without this a stale "transient" from ten seconds ago
 * could explain an unrelated later 401 (a genuinely revoked session, a wrong audience) and buy it a
 * pointless refresh attempt — the record must only ever describe the request that is failing NOW.
 *
 * `sinceSequence` makes that one-way: pass the value `renewalSequence()` returned BEFORE the renewal
 * started and a success can only retire failures that were already there when it began, never one
 * recorded while it was in flight by a different, overlapping renewal. Called with no argument it clears
 * unconditionally, which is what a caller with no renewal of its own (a test, a reset) wants.
 */
export function clearRenewalFailure(sinceSequence?: number): void {
  if (sinceSequence !== undefined && sinceSequence !== sequence) return;
  record = null;
}

/**
 * Read the recorded failure, but only while it is still fresh (default 15s). Reading does NOT consume
 * it: several concurrent requests can 401 off the same failed renewal and every one of them must see
 * the same verdict, otherwise the first 401 gets a retry and the rest get the overlay.
 */
export function readRenewalFailure(maxAgeMs: number = DEFAULT_MAX_AGE_MS): RenewalFailure | null {
  if (!record) return null;
  if (Date.now() - record.at > maxAgeMs) return null;
  return record.kind;
}

/** TEST-ONLY: drop the record AND rewind the sequence so cases can't leak across each other (mirrors
 *  __resetSessionExpiredForTest). */
export function __resetTokenRenewalForTest(): void {
  record = null;
  sequence = 0;
}
